import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import LoadingScreen from '../components/LoadingScreen'
import QuizEditor from '../components/courses/QuizEditor'
import { coursePath, courseProblems, STUDIO_PATH } from '../components/courses/courseUtils'
import { coursesAPI, monetizationAPI } from '../services/api'
import type { Course, CourseModule, CourseProblem, CourseQuestion, Lesson, SubscriptionTier } from '../types'
import { apiErrorMessage } from '../utils/apiError'

interface ModuleWithLessons extends CourseModule {
  lessons: Lesson[]
}

const byOrder = <T extends { display_order: number; id: number }>(a: T, b: T) =>
  a.display_order - b.display_order || a.id - b.id

const lines = (text: string) =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)

/** What the posting check found, and where; shown when publishing or saving is refused. */
export const ProblemsList = ({ problems }: { problems: CourseProblem[] }) => (
  <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-testid="course-problems">
    <p className="font-medium text-amber-900">Fix these first:</p>
    <ul className="mt-1 list-disc space-y-1 pl-5 text-amber-900">
      {problems.map((p, i) => (
        <li key={i}>
          <span className="font-medium">{p.where}:</span> {p.message}
        </li>
      ))}
    </ul>
  </div>
)

/** The error of a refused save: the problem list for the posting check, the reason otherwise. */
const useRefusal = () => {
  const [problems, setProblems] = useState<CourseProblem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const clear = () => {
    setProblems(null)
    setError(null)
  }
  const show = (err: unknown, fallback: string) => {
    const found = courseProblems(err)
    if (found) setProblems(found)
    else setError(apiErrorMessage(err, fallback))
  }
  const view = (
    <>
      {problems && <ProblemsList problems={problems} />}
      {error && (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      )}
    </>
  )
  return { clear, show, view }
}

interface LessonEditorProps {
  moduleId: number
  lesson?: Lesson
  onSaved: () => void
  onCancel: () => void
}

/** Add or change a lesson: text (Markdown) and/or a YouTube or Vimeo link, and whether it's a free preview. */
export const LessonEditor = ({ moduleId, lesson, onSaved, onCancel }: LessonEditorProps) => {
  const [title, setTitle] = useState(lesson?.title || '')
  const [kind, setKind] = useState<'text' | 'video'>(lesson?.lesson_type === 'video' ? 'video' : 'text')
  const [videoUrl, setVideoUrl] = useState(lesson?.video_url || '')
  const [content, setContent] = useState(lesson?.content || '')
  const [preview, setPreview] = useState(Boolean(lesson?.is_preview))
  const [minutes, setMinutes] = useState(lesson?.duration_minutes ? String(lesson.duration_minutes) : '')
  const [busy, setBusy] = useState(false)
  const refusal = useRefusal()

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    refusal.clear()
    const fields = {
      title: title.trim(),
      lesson_type: kind,
      content: content.trim() || null,
      video_url: kind === 'video' ? videoUrl.trim() || null : null,
      is_preview: preview,
      duration_minutes: minutes ? Number(minutes) : null,
    }
    try {
      if (lesson) await coursesAPI.updateLesson(lesson.id, fields)
      else await coursesAPI.addLesson(moduleId, fields)
      onSaved()
    } catch (err) {
      refusal.show(err, "Couldn't save the lesson.")
      setBusy(false)
    }
  }

  return (
    <form onSubmit={save} className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3" aria-label="Lesson">
      <input
        aria-label="Lesson title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={200}
        placeholder="Lesson title"
        className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
      />
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-1">
          <input type="radio" checked={kind === 'text'} onChange={() => setKind('text')} /> Text
        </label>
        <label className="flex items-center gap-1">
          <input type="radio" checked={kind === 'video'} onChange={() => setKind('video')} /> Video
        </label>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={preview} onChange={(e) => setPreview(e.target.checked)} /> Free preview
        </label>
        <label className="flex items-center gap-1">
          <input
            aria-label="Minutes"
            type="number"
            min={1}
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            className="w-16 rounded border border-gray-300 px-1"
          />
          min
        </label>
      </div>
      {kind === 'video' && (
        <input
          aria-label="Video link"
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          placeholder="YouTube or Vimeo link (an unlisted video works)"
          className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
        />
      )}
      <textarea
        aria-label="Lesson text"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={8}
        placeholder="The lesson (Markdown). The tutor answers students' questions from this text."
        className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm"
      />
      {refusal.view}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy || !title.trim()}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save lesson'}
        </button>
        <button type="button" onClick={onCancel} className="px-2 text-sm text-gray-600">
          Cancel
        </button>
      </div>
    </form>
  )
}

/** Free, or included with some of the creator's active tiers. */
const AccessSettings = ({
  course,
  tiers,
  onSaved,
}: {
  course: Course
  tiers: SubscriptionTier[]
  onSaved: (course: Course) => void
}) => {
  const [chosen, setChosen] = useState<number[]>(course.included_tier_ids)
  const [mode, setMode] = useState<'free' | 'subscribers'>(course.included_tier_ids.length ? 'subscribers' : 'free')
  const [busy, setBusy] = useState(false)
  const refusal = useRefusal()
  const active = tiers.filter((t) => t.is_active)

  const save = async () => {
    setBusy(true)
    refusal.clear()
    try {
      onSaved(
        await coursesAPI.update(course.id, {
          included_tier_ids: mode === 'free' ? [] : chosen,
          is_free: mode === 'free',
        })
      )
    } catch (err) {
      refusal.show(err, "Couldn't save who can join.")
    } finally {
      setBusy(false)
    }
  }

  const unchanged =
    (mode === 'free' && course.access === 'free') ||
    (mode === 'subscribers' &&
      chosen.length === course.included_tier_ids.length &&
      chosen.every((id) => course.included_tier_ids.includes(id)))

  return (
    <section className="space-y-2 rounded-lg bg-white p-5 shadow" aria-labelledby="course-access">
      <h2 id="course-access" className="font-semibold text-gray-900">
        Who can join
      </h2>
      <label className="flex items-center gap-2 text-sm">
        <input type="radio" checked={mode === 'free'} onChange={() => setMode('free')} /> Anyone, free
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="radio"
          checked={mode === 'subscribers'}
          onChange={() => setMode('subscribers')}
          disabled={active.length === 0}
        />{' '}
        Subscribers on these tiers
      </label>
      {active.length === 0 ? (
        <p className="pl-6 text-xs text-gray-500">
          You have no active subscription tiers. <Link to="/monetization" className="text-primary-700 underline">Set one up</Link>{' '}
          to include a course with it.
        </p>
      ) : (
        mode === 'subscribers' && (
          <div className="space-y-1 pl-6">
            {active.map((tier) => (
              <label key={tier.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={chosen.includes(tier.id)}
                  onChange={(e) =>
                    setChosen((list) => (e.target.checked ? [...list, tier.id] : list.filter((id) => id !== tier.id)))
                  }
                />
                {tier.name}
              </label>
            ))}
          </div>
        )
      )}
      <p className="text-xs text-gray-500">
        A subscriber who stops paying keeps their progress but not the lessons. Selling a single course isn&rsquo;t
        available yet.
      </p>
      {refusal.view}
      <button
        type="button"
        onClick={save}
        disabled={busy || unchanged || (mode === 'subscribers' && chosen.length === 0)}
        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save'}
      </button>
    </section>
  )
}

/** The course's own words: title, subtitle, description, what students learn. */
const DetailsSettings = ({ course, onSaved }: { course: Course; onSaved: (course: Course) => void }) => {
  const [title, setTitle] = useState(course.title)
  const [subtitle, setSubtitle] = useState(course.subtitle || '')
  const [description, setDescription] = useState(course.description)
  const [objectives, setObjectives] = useState(course.learning_objectives.join('\n'))
  const [busy, setBusy] = useState(false)
  const refusal = useRefusal()

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    refusal.clear()
    try {
      onSaved(
        await coursesAPI.update(course.id, {
          title: title.trim(),
          subtitle: subtitle.trim(), // "" clears it: the server skips nulls
          description: description.trim(),
          learning_objectives: lines(objectives),
        })
      )
    } catch (err) {
      refusal.show(err, "Couldn't save the course.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={save} className="space-y-3 rounded-lg bg-white p-5 shadow" aria-label="Course details">
      <h2 className="font-semibold text-gray-900">Details</h2>
      <label className="block text-sm">
        <span className="text-gray-700">Title</span>
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
      </label>
      <label className="block text-sm">
        <span className="text-gray-700">Subtitle</span>
        <input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
      </label>
      <label className="block text-sm">
        <span className="text-gray-700">What it covers</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
        />
      </label>
      <label className="block text-sm">
        <span className="text-gray-700">What students will learn (one per line)</span>
        <textarea
          value={objectives}
          onChange={(e) => setObjectives(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
        />
      </label>
      {refusal.view}
      <button
        type="submit"
        disabled={busy || !title.trim() || !description.trim()}
        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save details'}
      </button>
    </form>
  )
}

/** What students asked the tutor: the question, answer, lesson and date; never who asked. */
const StudentQuestions = ({ courseId }: { courseId: number }) => {
  const [questions, setQuestions] = useState<CourseQuestion[] | null>(null)
  useEffect(() => {
    coursesAPI
      .courseQuestions(courseId)
      .then(setQuestions)
      .catch(() => setQuestions([]))
  }, [courseId])
  return (
    <section className="space-y-2 rounded-lg bg-white p-5 shadow" aria-labelledby="student-questions">
      <h2 id="student-questions" className="font-semibold text-gray-900">
        What students ask the tutor
      </h2>
      <p className="text-xs text-gray-500">Shown without names. Questions the lesson can&rsquo;t answer are worth a new lesson.</p>
      {questions === null ? null : questions.length === 0 ? (
        <p className="text-sm text-gray-500">No questions yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100" data-testid="student-questions">
          {questions.map((q) => (
            <li key={q.id} className="py-2 text-sm">
              <p className="text-xs text-gray-500">
                {q.lesson_title} · {new Date(q.created_at).toLocaleDateString()}
              </p>
              <p className="font-medium text-gray-900">{q.question}</p>
              <p className="mt-0.5 whitespace-pre-line text-gray-600">{q.answer}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * A course's editor (/courses/<id>): details, who can join, modules and
 * lessons, publishing, and what students ask the tutor. Publishing (and, once
 * live, any change) runs the posting check; what it finds is listed with
 * where it is.
 */
const CourseEditor = () => {
  const { id } = useParams()
  const courseId = Number(id)
  const [course, setCourse] = useState<Course | null>(null)
  const [modules, setModules] = useState<ModuleWithLessons[]>([])
  const [tiers, setTiers] = useState<SubscriptionTier[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [newModule, setNewModule] = useState('')
  const [editing, setEditing] = useState<{ moduleId: number; lessonId?: number } | null>(null)
  const [publishing, setPublishing] = useState(false)
  const [stats, setStats] = useState<{ students: number; done: Record<number, number> } | null>(null)
  const publishRefusal = useRefusal()
  const moduleRefusal = useRefusal()

  const loadOutline = useCallback(async () => {
    const list = (await coursesAPI.modules(courseId)).sort(byOrder)
    setModules(
      await Promise.all(list.map(async (m) => ({ ...m, lessons: (await coursesAPI.lessons(m.id)).sort(byOrder) })))
    )
  }, [courseId])

  useEffect(() => {
    Promise.all([coursesAPI.get(courseId), loadOutline()])
      .then(([c]) => setCourse(c))
      .catch((e) => setLoadError(apiErrorMessage(e, "Couldn't load the course.")))
    monetizationAPI
      .getTiers()
      .then(setTiers)
      .catch(() => undefined)
  }, [courseId, loadOutline])

  const live0 = course?.status === 'published'
  useEffect(() => {
    if (!live0) return
    coursesAPI
      .lessonStats(courseId)
      .then((s) => setStats({ students: s.students, done: Object.fromEntries(s.lessons.map((l) => [l.lesson_id, l.completed])) }))
      .catch(() => undefined)
  }, [courseId, live0])

  if (loadError) return <p className="text-sm text-danger-700">{loadError}</p>
  if (!course) return <LoadingScreen />
  const live = course.status === 'published'

  const addModule = async (e: FormEvent) => {
    e.preventDefault()
    moduleRefusal.clear()
    try {
      await coursesAPI.addModule(course.id, { title: newModule.trim(), display_order: modules.length })
      setNewModule('')
      await loadOutline()
    } catch (err) {
      moduleRefusal.show(err, "Couldn't add the module.")
    }
  }

  const act = async (run: () => Promise<unknown>, fallback: string) => {
    moduleRefusal.clear()
    try {
      await run()
      await loadOutline()
    } catch (err) {
      moduleRefusal.show(err, fallback)
    }
  }

  const publish = async () => {
    setPublishing(true)
    publishRefusal.clear()
    try {
      setCourse(await coursesAPI.publish(course.id))
    } catch (err) {
      publishRefusal.show(err, "Couldn't publish the course.")
    } finally {
      setPublishing(false)
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="course-editor">
      <header className="flex flex-wrap items-center gap-3">
        <Link to={STUDIO_PATH} className="text-sm text-primary-700 hover:underline">
          ← Courses
        </Link>
        <h1 className="w-full text-2xl font-bold text-gray-900">{course.title}</h1>
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${live ? 'bg-success-50 text-success-700' : 'bg-gray-100 text-gray-700'}`}
          data-testid="course-status"
        >
          {live ? 'Published' : 'Draft'}
        </span>
        <Link to={coursePath(course.slug)} className="text-sm text-primary-700 hover:underline">
          {live ? 'View the page' : 'Preview the page'}
        </Link>
        {!live && (
          <button
            type="button"
            onClick={publish}
            disabled={publishing || modules.length === 0}
            className="ml-auto rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {publishing ? 'Checking…' : 'Publish'}
          </button>
        )}
      </header>
      {publishRefusal.view}
      {live && (
        <p className="text-xs text-gray-500">
          Live: changes are checked before they&rsquo;re saved, and lessons and modules are unpublished rather than deleted,
          since students may have progress in them.
        </p>
      )}

      <DetailsSettings course={course} onSaved={setCourse} />
      <AccessSettings course={course} tiers={tiers} onSaved={setCourse} />

      <section className="space-y-4 rounded-lg bg-white p-5 shadow" aria-labelledby="course-outline">
        <h2 id="course-outline" className="font-semibold text-gray-900">
          Modules and lessons
        </h2>
        {moduleRefusal.view}
        {modules.map((module) => (
          <div key={module.id} className="rounded-md border border-gray-200" data-testid="editor-module">
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
              <span className="font-medium text-gray-900">{module.title}</span>
              {!module.is_published && <span className="text-xs text-gray-500">(unpublished)</span>}
              <span className="ml-auto flex gap-3 text-xs">
                {live ? (
                  <button
                    type="button"
                    onClick={() =>
                      act(
                        () => coursesAPI.updateModule(module.id, { is_published: !module.is_published }),
                        "Couldn't change the module."
                      )
                    }
                    className="text-gray-600 hover:underline"
                  >
                    {module.is_published ? 'Unpublish' : 'Publish'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => act(() => coursesAPI.deleteModule(module.id), "Couldn't delete the module.")}
                    className="text-danger-700 hover:underline"
                  >
                    Delete module
                  </button>
                )}
              </span>
            </div>
            <ul className="divide-y divide-gray-100">
              {module.lessons.map((lesson) =>
                editing?.lessonId === lesson.id && lesson.lesson_type === 'quiz' && lesson.quiz_id ? (
                  <li key={lesson.id} className="p-2">
                    <QuizEditor quizId={lesson.quiz_id} live={live} onClose={() => setEditing(null)} />
                  </li>
                ) : editing?.lessonId === lesson.id ? (
                  <li key={lesson.id} className="p-2">
                    <LessonEditor
                      moduleId={module.id}
                      lesson={lesson}
                      onCancel={() => setEditing(null)}
                      onSaved={async () => {
                        setEditing(null)
                        await loadOutline()
                      }}
                    />
                  </li>
                ) : (
                  <li key={lesson.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid="editor-lesson">
                    <span className="text-gray-900">{lesson.title}</span>
                    <span className="text-xs text-gray-500">{lesson.lesson_type}</span>
                    {lesson.is_preview && <span className="rounded bg-sky-50 px-1.5 text-xs text-sky-700">Free preview</span>}
                    {!lesson.is_published && <span className="text-xs text-gray-500">(unpublished)</span>}
                    {stats && stats.students > 0 && (
                      <span className="text-xs text-gray-500" data-testid="lesson-done-count">
                        {stats.done[lesson.id] ?? 0} of {stats.students} done
                      </span>
                    )}
                    <span className="ml-auto flex gap-3 text-xs">
                      <button
                        type="button"
                        onClick={() => setEditing({ moduleId: module.id, lessonId: lesson.id })}
                        className="text-primary-700 hover:underline"
                      >
                        Edit
                      </button>
                      {live ? (
                        <button
                          type="button"
                          onClick={() =>
                            act(
                              () => coursesAPI.updateLesson(lesson.id, { is_published: !lesson.is_published }),
                              "Couldn't change the lesson."
                            )
                          }
                          className="text-gray-600 hover:underline"
                        >
                          {lesson.is_published ? 'Unpublish' : 'Publish'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => act(() => coursesAPI.deleteLesson(lesson.id), "Couldn't delete the lesson.")}
                          className="text-danger-700 hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </span>
                  </li>
                )
              )}
            </ul>
            <div className="p-2">
              {editing?.moduleId === module.id && editing.lessonId === undefined ? (
                <LessonEditor
                  moduleId={module.id}
                  onCancel={() => setEditing(null)}
                  onSaved={async () => {
                    setEditing(null)
                    await loadOutline()
                  }}
                />
              ) : (
                <span className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setEditing({ moduleId: module.id })}
                    className="text-sm text-primary-700 hover:underline"
                  >
                    + Add a lesson
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      act(async () => {
                        const quiz = await coursesAPI.addQuizLesson(module.id, { title: `${module.title}: check` })
                        setEditing({ moduleId: module.id, lessonId: quiz.id })
                      }, "Couldn't add the quiz.")
                    }
                    className="text-sm text-primary-700 hover:underline"
                  >
                    + Add a quiz
                  </button>
                </span>
              )}
            </div>
          </div>
        ))}
        <form onSubmit={addModule} className="flex gap-2">
          <input
            aria-label="New module title"
            value={newModule}
            onChange={(e) => setNewModule(e.target.value)}
            placeholder={modules.length ? 'Next module' : 'First module, e.g. "The basics"'}
            className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <button
            type="submit"
            disabled={!newModule.trim()}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            Add module
          </button>
        </form>
      </section>

      {live && <StudentQuestions courseId={course.id} />}
    </div>
  )
}

export default CourseEditor
